import type { ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Notification } from "@/components/admin/notification";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext";
import { LanguageSelector } from "./LanguageSelector";

export type AuthLayoutProps = {
  /** Heading inside the card (e.g. "Sign in"). */
  title: ReactNode;
  /** Optional line below the heading. */
  subtitle?: ReactNode;
  /** Form, buttons and links rendered in the card body. */
  children: ReactNode;
};

/**
 * Shared shell for the auth screens (login, signup, password flows).
 *
 * Renders the full-screen backdrop, the glass card, the app logo and the
 * language switcher, plus a single <Notification /> — pages wrapped in this
 * layout must not render their own.
 */
export const AuthLayout = ({ title, subtitle, children }: AuthLayoutProps) => {
  const { darkModeLogo, title: appTitle } = useConfigurationContext();

  return (
    <div
      className="min-h-screen flex items-start justify-center p-6 pt-12"
      style={{
        // Opaque base under the photo. Everything on the card is white or
        // near-white, so without it the form is white-on-white for as long as
        // the 460 KB backdrop takes to paint — and indefinitely offline.
        backgroundColor: "#0f172a",
        backgroundImage: "url(/ai-brain-bg.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="w-full max-w-md">
        <Card className="w-full bg-white/10 backdrop-blur-md border border-white/20 shadow-2xl">
          <CardHeader className="text-center relative">
            <div className="absolute top-0 right-4">
              <LanguageSelector />
            </div>
            <div className="flex justify-center mb-4">
              <div className="p-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20">
                {/* Always the dark-mode (light-coloured) logo: the card sits on
                    the dark photo backdrop in both colour schemes. */}
                <img src={darkModeLogo} alt={appTitle} className="w-16 h-16" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-white">
              {title}
            </CardTitle>
            {subtitle ? <p className="text-gray-200">{subtitle}</p> : null}
          </CardHeader>
          <CardContent>{children}</CardContent>
        </Card>
      </div>
      <Notification />
    </div>
  );
};
